import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Grid,
  MenuItem,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import Autocomplete from '@material-ui/lab/Autocomplete';
import Alert from '@material-ui/lab/Alert';
import TextField from '@material-ui/core/TextField';
import { useContext, useEffect, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import Fuse from 'fuse.js';
import * as XLSX from 'xlsx';
import { getFileList, addFileToQueue, getDistributorList, downloadIngestionFile, cancelIngestionFile, getPreview, getCanonicalPreview, confirmManualUpload, dryRunImport, markIngestionFileImported, getRouteDecision, sqlSkipRows, sqlRevalidate, getTransformDistributorList, stagePreview, stagedDryRun, stagedImport, getFileProcessingCost, repriceFileProcessingCost, getAiCreditMeter, triageFileFeedback, applyFileRemap, reprocessFileWithFeedback, reprocessIngestionFile, detectMapping, getHouseErp, setDistributorAutoImport, detectVendors } from '../../Actions';
import BulkUpload from './BulkUpload';
import { fingerprintFile, forceIsoDateCells, listDataSheets } from './fingerprint';
import ResolveCodesDialog, { applySkips } from '../Shared/ResolveCodesDialog';
import { AppContext } from '../../Context/AppContextProvide';
import { useAlert } from '../../Context/AlertContextProvider';
import { formatDttm, dttmToEpoch } from '../../Util/timezone';

// Our own upload template, as a selectable file spec (server migration v2.17.0 creates it from the
// legacy _SHOWROOM PRICING_ JSON transform). Compared on a normalized name for the same reason the
// server does: instances that were converted by hand may have spaced or underscored it differently.
const isNativeTemplateName = (name) =>
  String(name ?? '').toLowerCase().replace(/[^a-z0-9]/g, '') === 'showroompricing';

// Passthrough transform every AI-processed file is imported under (its output is already canonical,
// so no vendor mapping applies). Must stay in sync with PASSTHROUGH_TRANSFORM on the server.
const SHOWROOM_PRICING_TRANSFORM = '_SHOWROOM PRICING_';

function formatTokens(n) {
  if (!n) return '—';
  return n.toLocaleString();
}

// Customer-facing status labels. 'FILE PROCESSED' is the pipeline's name for "the processor
// finished", but on this page it reads as a terminal state when it actually means "waiting for
// you to preview / dry-run / import" — so label it by the action it awaits. Display-only: the
// underlying status strings are a processor/server contract and existing DB rows keep them.
const STATUS_LABELS = { 'FILE PROCESSED': 'READY TO IMPORT' };
const statusLabel = (s) => STATUS_LABELS[String(s ?? '').toUpperCase()] ?? s;

// Saved import impact (from ingestion_file.import_metrics). Returns the object or null.
function parseMetrics(m) {
  if (!m) return null;
  try { return typeof m === 'string' ? JSON.parse(m) : m; } catch { return null; }
}
const METRIC_LABELS = [
  ['newProducts', 'New products'],
  ['updatedProducts', 'Products updated'],
  ['printedProductsUpdated', 'Printed products updated'],
  ['newSkus', 'New colors / SKUs'],
  ['newPrices', 'New prices'],
  ['updatedPrices', 'Prices updated'],
  ['removedPrices', 'Prices removed'],
];

function formatDuration(ms) {
  if (!ms) return '—';
  if (ms < 1000) return `${ms}ms`;
  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}

export default function FileProcessing(props) {
  const { user } = useContext(AppContext);
  const { handleAlertOpen, setMessage, setMessageType } = useAlert();
  const isHidden = !!user?.is_hidden;
  const [refreshData, setRefreshData] = useState(true);
  const [distributorList, setDistributorList] = useState([]);
  const [fileList, setFileList] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedDistributor, setSelectedDistributor] = useState('');
  const [fuzzyMatches, setFuzzyMatches] = useState([]);
  const [fuzzyDialogOpen, setFuzzyDialogOpen] = useState(false);
  // The mapping this site imports most files through (from its own history). Null on sites that
  // don't use one, which is most of them — the banner only exists for ERP customers.
  const [houseErp, setHouseErp] = useState(null);
  // Header-signature match for the file currently in the dialog: { transform, strength, ... }.
  const [mappingHint, setMappingHint] = useState(null);
  const [mappingHintLoading, setMappingHintLoading] = useState(false);
  // Set when the user hits Upload while a hint is showing — holds the vendor name until they confirm
  // they really want AI. Nothing is uploaded while this is open.
  const [aiConfirmName, setAiConfirmName] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  // List controls: default to everything still in play, so finished files from months of imports
  // stay out of the way without hiding anything you might still act on.
  const [statusFilter, setStatusFilter] = useState('active');
  const [searchText, setSearchText] = useState('');
  // AI cost per file (hidden reveal), fetched on demand. Keyed by ingestion_file uuid.
  const [costByUuid, setCostByUuid] = useState({});
  const [costLoadingUuid, setCostLoadingUuid] = useState(null);
  const [repricingUuid, setRepricingUuid] = useState(null);
  // Customer-facing credit meter (points only).
  const [creditMeter, setCreditMeter] = useState(null);
  // Feedback / correction flow — lives inside the preview dialog so the user can type while viewing
  // the data. Keyed off previewFile.
  const [feedbackText, setFeedbackText] = useState('');
  const [triageResult, setTriageResult] = useState(null);
  const [feedbackBusy, setFeedbackBusy] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [cancelDialogFile, setCancelDialogFile] = useState(null);
  const [reprocessDialogFile, setReprocessDialogFile] = useState(null);
  const [reprocessingUuid, setReprocessingUuid] = useState(null);
  const [importDialogFile, setImportDialogFile] = useState(null);
  // Duplicate warnings carried from the dry run into the import confirm (soft warning only).
  // Set only on the Dry Run -> Import transition, cleared whenever the import dialog closes.
  const [importDupWarnings, setImportDupWarnings] = useState(null);
  // Vendor detection for the selected file (same engine as the bulk drop). Pre-fills the vendor
  // box and is sent along with the upload so the server can measure suggestion vs. actual choice.
  const [vendorDetect, setVendorDetect] = useState(null);
  const [vendorDetectLoading, setVendorDetectLoading] = useState(false);
  // Multi-tab spreadsheets: the workbook's non-empty tabs and the one the user picked to process.
  // Template workbooks carry instruction/example/code tabs beside the price list — without a pick
  // every tab gets processed as pricing.
  const [sheetOptions, setSheetOptions] = useState([]);
  const [selectedSheet, setSelectedSheet] = useState('');
  // Tracked only to guard the import flow; the row's spinner comes from the IMPORTING status.
  const [, setImportingUuid] = useState(null);
  const [dryRunFile, setDryRunFile] = useState(null);
  const [dryRunResult, setDryRunResult] = useState(null);
  const [dryRunLoading, setDryRunLoading] = useState(false);
  const [resolveOpen, setResolveOpen] = useState(false);
  const [resolveIssues, setResolveIssues] = useState([]);
  const [resolveAction, setResolveAction] = useState(null);
  const [resolveFile, setResolveFile] = useState(null);
  // For legacy mapping files, tracks how to resolve unknown codes: SQL (server-staged, by fileName)
  // vs JSON (in-memory). Null for the AI passthrough path (always JSON).
  const [resolveCtx, setResolveCtx] = useState(null);
  const [previewFile, setPreviewFile] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  // Mapping (legacy-engine) uploads are synchronous and not tracked in ingestion_file, so they live
  // in client state and are merged into the file list. mappingBusy shows a spinner while the initial
  // transform/preview runs (SQL staging on large ERP files can take a while).
  const [mappingFiles, setMappingFiles] = useState([]);
  const [mappingBusy, setMappingBusy] = useState(false);
  const fuseRef = useRef(null);
  const isDistributorNameValErr = /[^a-zA-Z0-9\s'_-]/g.test(selectedDistributor) && !distributorList?.find(d => d.distributor_name === selectedDistributor);

  useEffect(() => {
    async function getPageData() {
      const fileRes = await getFileList().catch(() => null);
      setFileList(fileRes?.data ?? []);

      getAiCreditMeter()
        .then((r) => setCreditMeter(r.data))
        .catch(() => setCreditMeter(null));

      // Unified vendor list = AI distributors + every active mapping name (real distributors plus
      // ERP exports like RFMS / QFloors that live only in file_upload_transform). Merged by name so
      // mapped vendors are selectable; the server pre-flight decides mapping vs AI per upload.
      const [distributorRes, transformRes, houseErpRes] = await Promise.all([
        getDistributorList().catch(() => null),
        // ALL mapping types, not just SPEC: submitWithName already routes JSON/SQL through
        // startMappingFlow, so leaving them out of the list only meant an ERP customer couldn't pick
        // the mapping they were supposed to use and reached for the vendor name instead.
        getTransformDistributorList('SPEC,SQL,JSON').catch(() => null),
        getHouseErp().catch(() => null),
      ]);
      setHouseErp(houseErpRes?.data?.mappings?.[0] ?? null);
      const aiList = distributorRes?.data ?? [];
      // The passthrough is an internal import mechanism, not something anyone selects.
      const transforms = (transformRes?.data?.data ?? [])
        .filter((t) => t.distributor_name !== SHOWROOM_PRICING_TRANSFORM);
      // Name -> transform_type (SPEC / SQL / JSON) so we can chip a choice as a transform vs a plain
      // vendor (which just supplies AI context data). A name can be both; transform wins for the chip
      // since that's what the dispatch routes on.
      const transformTypeByName = {};
      for (const t of transforms) if (t.distributor_name) transformTypeByName[t.distributor_name] = t.transform_type || 'TRANSFORM';

      const byName = new Map();
      for (const d of aiList) if (d?.distributor_name && !byName.has(d.distributor_name)) byName.set(d.distributor_name, { ...d });
      for (const t of transforms) if (t.distributor_name && !byName.has(t.distributor_name)) byName.set(t.distributor_name, { distributor_name: t.distributor_name, distributor_uuid: '' });
      // Mapped imports sort to the top as one block. MUI's groupBy renders groups in list order, so
      // this sort IS the grouping — and it puts "RFMS" in front of someone who would otherwise
      // scroll past it looking for the vendor name printed on their export. Our own template leads
      // the group: it is the right answer for any file built from the downloadable template, which
      // is the most common thing anyone uploads here.
      const listRank = (d) => {
        if (isNativeTemplateName(d.distributor_name)) return 0;
        return d.transformType ? 1 : 2;
      };
      const mergedList = Array.from(byName.values())
        .map((d) => ({ ...d, transformType: transformTypeByName[d.distributor_name] || null }))
        .sort((a, b) => listRank(a) - listRank(b) || a.distributor_name.localeCompare(b.distributor_name));
      setDistributorList(mergedList);

      fuseRef.current = mergedList.length ? new Fuse(
        mergedList,
        { 
          keys: ['distributor_name'], 
          threshold: 0.2, 
          isCaseSensitive: false, 
          shouldSort: true, 
          ignoreLocation: true, 
          includeScore: true 
        }
      ) : null;
    }

    if (refreshData) {
      getPageData();
      setRefreshData(false);
    }
  }, [refreshData])

  // Auto-refresh while any file is in a transient state (importing / processing / queued) so the
  // background import and processor progress update on their own.
  useEffect(() => {
    const transient = new Set(['importing', 'processing', 'waiting in queue']);
    const anyTransient = (fileList ?? []).some((f) => transient.has((f.status ?? '').toLowerCase()));
    if (!anyTransient) return undefined;
    const t = setInterval(() => setRefreshData(true), 5000);
    return () => clearInterval(t);
  }, [fileList])

  function triggerBlobDownload(blob, filename) {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || 'download';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => window.URL.revokeObjectURL(url), 100);
  }

  function triggerCsvDownload(blob, filename) {
    const baseName = (filename || 'download').replace(/\.[^/.]+$/, '');
    triggerBlobDownload(blob, `${baseName}.csv`);
  }

  // Turn a preview (array-of-arrays: row 0 = header) into CSV text.
  function previewToCsv(dataPreview) {
    return (dataPreview ?? [])
      .map((row) => row
        .map((cell) => {
          const s = cell == null ? '' : String(cell);
          return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
        })
        .join(','))
      .join('\n');
  }

  async function handleDownload(file) {
    // Mapping files have no S3 object — build the CSV from their resolved (transformed) preview.
    if (file.mapping) {
      const csv = previewToCsv(file.resolvedPreview?.dataPreview);
      if (!csv) { notify('error', 'Nothing to download.'); return; }
      triggerCsvDownload(new Blob([csv], { type: 'text/csv' }), file.filename);
      return;
    }
    const res = await downloadIngestionFile(file.ingestion_file_uuid).catch(() => null);
    if (!res) return;
    triggerCsvDownload(new Blob([res.data]), file.filename);
  }

  // The file the vendor actually sent, in its original format (pdf/xlsx/csv/...) — the processor
  // retains it next to its output, so it stays downloadable even after the import.
  async function handleDownloadOriginal(file) {
    const res = await downloadIngestionFile(file.ingestion_file_uuid, 'original').catch(() => null);
    if (!res) { notify('error', 'Original file not found.'); return; }
    triggerBlobDownload(new Blob([res.data]), file.filename);
  }

  // Rows the processor rejected during validation (excluded from the import), with a
  // reject_reasons column — delivered alongside processed.csv when any rows failed.
  async function handleDownloadRejected(file) {
    const res = await downloadIngestionFile(file.ingestion_file_uuid, 'rejected').catch(() => null);
    if (!res) { notify('error', 'Rejected-rows file not found.'); return; }
    triggerCsvDownload(new Blob([res.data]), `rejected_${file.filename.replace(/\.[^.]+$/, '')}.csv`);
  }

  // Per-vendor hands-off import. The setting lives on the vendor, so flipping it from any of the
  // vendor's file rows affects every future file from that vendor.
  async function handleAutoImportToggle(file, enabled) {
    if (!file.distributor_uuid) return;
    const res = await setDistributorAutoImport(file.distributor_uuid, enabled).catch(() => null);
    if (!res) { notify('error', 'Could not update the auto-import setting.'); return; }
    setDistributorList((list) => (list ?? []).map((d) =>
      d.distributor_uuid === file.distributor_uuid ? { ...d, auto_import_enabled: enabled ? 1 : 0 } : d
    ));
    notify('success', `Auto-import ${enabled ? 'enabled' : 'disabled'} for ${file.distributor_name}. ${enabled ? 'Clean processed files from this vendor will import without waiting for you.' : ''}`);
  }

  function notify(type, msg) {
    setMessageType(type);
    setMessage(msg);
    handleAlertOpen();
  }

  // Does the chosen file's header row match one of this site's mapped imports? The file is parsed
  // here in the browser — the same fingerprint the bulk drop builds — so only the header row travels.
  //
  // Deliberately keyed on the FILE alone, not the vendor: the vendor only affects the server's
  // `selected` echo, and re-running this on every keystroke in the vendor box would fingerprint the
  // file over and over. Whether the current vendor makes the hint worth showing is decided locally,
  // in showMappingNudge below.
  // Tab discovery runs once per file; the detection effect below re-runs when the pick changes.
  useEffect(() => {
    setSheetOptions([]);
    setSelectedSheet('');
    if (!selectedFile) return undefined;
    let cancelled = false;
    (async () => {
      const sheets = await listDataSheets(selectedFile).catch(() => []);
      if (cancelled || sheets.length < 2) return;
      setSheetOptions(sheets);
      // Default to ALL tabs ('' = no restriction): vendor workbooks routinely spread real pricing
      // across tabs (one per line/brand), and the AI pipeline has always handled that. Picking a
      // single tab is the opt-in for template/scratch-tab workbooks.
      setSelectedSheet('');
    })();
    return () => { cancelled = true; };
  }, [selectedFile]);

  useEffect(() => {
    if (!selectedFile) {
      setMappingHint(null);
      setMappingHintLoading(false);
      setVendorDetect(null);
      setVendorDetectLoading(false);
      return undefined;
    }
    let cancelled = false;
    setMappingHint(null);
    setMappingHintLoading(true);
    setVendorDetect(null);
    setVendorDetectLoading(true);

    (async () => {
      const fingerprint = await fingerprintFile(selectedFile, 0, selectedSheet || undefined).catch(() => null);
      const headers = (fingerprint?.columns ?? []).map((c) => c.header).filter(Boolean);
      if (cancelled) return;

      // Vendor detection runs even without headers — PDFs still yield filename/folder signals.
      // Same endpoint the bulk drop scores with; one file is just a batch of one.
      if (fingerprint) {
        detectVendors([fingerprint])
          .then((r) => {
            if (cancelled) return;
            const result = r?.data?.files?.[0] ?? null;
            setVendorDetect(result);
            setVendorDetectLoading(false);
            // Pre-fill only an EMPTY vendor box — never overwrite something the user typed.
            const guess = result?.guess;
            if (guess?.distributorName) {
              setSelectedDistributor((prev) => (prev ? prev : guess.distributorName));
            }
          })
          .catch(() => { if (!cancelled) { setVendorDetect(null); setVendorDetectLoading(false); } });
      } else {
        setVendorDetectLoading(false);
      }

      if (!headers.length) {
        // PDFs, images and unparseable workbooks have no headers to match on.
        setMappingHintLoading(false);
        return;
      }
      const res = await detectMapping(headers, selectedFile.name, selectedDistributor).catch(() => null);
      if (cancelled) return;
      setMappingHintLoading(false);
      // engine 'mapping' means the upload is already headed for one (filename history usually), so
      // there is nothing to warn about no matter what the vendor box says.
      if (res?.data?.suggestion && res.data.selected?.engine !== 'mapping') setMappingHint(res.data.suggestion);
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFile, selectedSheet]);

  // The vendor currently typed/picked, when it is one of this site's mappings.
  const selectedMapping = distributorList?.find(
    (d) => d.distributor_name === selectedDistributor && d.transformType
  );
  // Warn only when the file looks like a mapped import AND the current selection would send it to AI
  // anyway. Picking a different mapping is a deliberate choice between mappings — not our business.
  const showMappingNudge = Boolean(mappingHint) && !selectedMapping;
  // What the user actually loses by choosing AI depends on the mapping's matching strategy, so the
  // warning has to read it rather than assert the ERP case at everyone. A mapping with a
  // matching_type (rfms_*, pacific_*) matches on the ERP's own ids, which AI cannot reproduce — that
  // is the duplicate-products case. A plain mapping matches the same way AI does, so claiming
  // duplicates there would be untrue, and a warning that overstates itself gets ignored everywhere.
  const mappingUsesIdMatching = Boolean(mappingHint?.matchingType);
  const mappingIsFullRefresh = /_full$/.test(mappingHint?.matchingType || '');
  // Our own template is a stronger claim than "resembles a vendor mapping": the columns are not
  // merely similar to something, they are ours, and the vendor is already inside the file.
  const mappingIsNative = Boolean(mappingHint?.isNative);

  // Is this ingestion_file a processor SPEC (rule-builder) file? Those use server-side staging for
  // preview/dry-run/import; AI files use the _SHOWROOM PRICING_ passthrough below.
  async function getSpecInfo(file) {
    if (file.mapping) return { isSpec: false };
    const d = await getRouteDecision(file.distributor_name, file.filename)
      .then((r) => r.data)
      .catch(() => null);
    return { isSpec: d?.engine === 'mapping' && d?.transformType === 'SPEC' };
  }

  // Parse a downloaded CSV/xlsx Blob into a 2-D dataPreview array ([headerRow, ...rows]).
  // CSVs (our own canonical output) are read VERBATIM: SheetJS's default handling re-interprets
  // cell text — its fuzzy date inference turned the size `3 1/4"` into "3/1/04", and its codepage
  // guess mangled UTF-8 (`3 1/8”` → `3 1/8â€`). Decode as UTF-8 and parse with raw:true so every
  // cell stays exactly the string the processor wrote. Real xlsx blobs (zip signature) keep the
  // array path, with date cells normalized to ISO like every other parser in the app.
  async function blobToDataPreview(blob) {
    const buf = await blob.arrayBuffer();
    const bytes = new Uint8Array(buf);
    const isXlsx = bytes[0] === 0x50 && bytes[1] === 0x4b; // zip 'PK' signature
    const wb = isXlsx
      ? XLSX.read(bytes, { type: 'array', cellDates: true })
      : XLSX.read(new TextDecoder('utf-8').decode(bytes), { type: 'string', raw: true });
    const ws = wb.Sheets[wb.SheetNames[0]];
    if (isXlsx) forceIsoDateCells(ws);
    return XLSX.utils.sheet_to_json(ws, { header: 1, defval: '', raw: false, blankrows: false });
  }

  // Preview payload for a file.
  //  - Mapping files (Phase B, legacy inline JSON/SQL): run the ORIGINAL upload through the vendor
  //    transform (columnTransform maps source→canonical, validates against master_code_translation_vw).
  //  - AI files: the processor output is ALREADY canonical, so validate it directly against the
  //    canonical material_class_cd / uom_type_cd / basis_cd tables via canonical-preview. That returns
  //    the unresolved-code issues[] that drive ResolveCodesDialog — the mapping-dropdown step that the
  //    _SHOWROOM PRICING_ passthrough (synonym-table only) was silently skipping.
  // (SPEC files are handled separately via server-side staging — see getSpecInfo / stage-* endpoints.)
  async function getProcessedPreview(file) {
    if (file.mapping) {
      const formData = new FormData();
      formData.append('dist', file.transform);
      formData.append('priceList', file.originalFile);
      formData.append('collectIssues', 'true');
      const preview = await getPreview(formData).catch(() => null);
      return preview?.data ?? null;
    }

    const dl = await downloadIngestionFile(file.ingestion_file_uuid).catch(() => null);
    if (!dl) return null;
    const baseName = (file.filename || file.ingestion_file_uuid).replace(/\.[^/.]+$/, '');
    const dataPreview = await blobToDataPreview(dl.data).catch(() => null);
    if (!dataPreview || dataPreview.length < 2) return null;

    const preview = await getCanonicalPreview(dataPreview, baseName).catch(() => null);
    if (!preview?.data) return null;
    // canonical-preview validates rows but knows nothing about vendors, so it returns no
    // `distributor`. /upload/file/confirm still requires one — it resolves the transform to pick a
    // matching strategy (getTransformType → route_based_on_transform). AI vendors have no
    // file_upload_transform row by definition (that is why they were routed to AI), so import under
    // the passthrough: no matching_type → standard JSON match, and the real vendor comes from the
    // output's own `distributor` column. Without this, confirm 404s on a missing transform.
    return { ...preview.data, distributor: SHOWROOM_PRICING_TRANSFORM };
  }

  // Open the resolve-unknown-codes dialog and remember what to resume afterward.
  function openResolve(issues, action, file) {
    setResolveIssues(issues);
    setResolveAction(action);
    setResolveFile(file);
    setResolveOpen(true);
  }

  async function handleDryRun(file, skipSpecs = []) {
    setDryRunFile(file);
    setDryRunResult(null);
    setDryRunLoading(true);

    // SPEC files: counts come from the server-side staged table (all rows), not a client sample.
    if (!file.mapping) {
      const { isSpec } = await getSpecInfo(file);
      if (isSpec) {
        const res = await stagedDryRun(file.ingestion_file_uuid, skipSpecs).catch(() => null);
        setDryRunLoading(false);
        if (!res) { setDryRunFile(null); notify('error', 'Dry run failed. Please try again.'); return; }
        // Unresolved material class / UOM / basis codes on the staged table: prompt to map/create/skip.
        if (res.data?.issues?.length) { setDryRunFile(null); openResolve(res.data.issues, 'dryrun', file); return; }
        setDryRunResult({ ...res.data, skippedRows: res.data?.skippedRows ?? 0 });
        return;
      }
    }

    // Mapping files arrive with an already-resolved preview (issues, incl. the SQL staged-table
    // loop, are handled before the dry run so we never re-stage). AI files derive it fresh here.
    let preview;
    if (file.mapping) {
      preview = file.resolvedPreview;
      if (!preview) {
        setDryRunLoading(false);
        setDryRunFile(null);
        notify('error', 'Dry run failed. Please try again.');
        return;
      }
    } else {
      const raw = await getProcessedPreview(file);
      if (!raw) {
        setDryRunLoading(false);
        setDryRunFile(null);
        notify('error', 'Dry run failed. Please try again.');
        return;
      }
      preview = applySkips(raw, skipSpecs);
      if (preview.issues?.length) {
        setDryRunLoading(false);
        setDryRunFile(null);
        openResolve(preview.issues, 'dryrun', file);
        return;
      }
      if (preview.dataPreview.length <= 1) {
        setDryRunLoading(false);
        setDryRunFile(null);
        notify('warning', 'All rows were skipped — nothing to preview.');
        return;
      }
    }

    const res = await dryRunImport(preview).catch(() => null);
    setDryRunLoading(false);
    if (!res) {
      setDryRunFile(null);
      notify('error', 'Dry run failed. Please try again.');
      return;
    }
    setDryRunResult({ ...res.data, skippedRows: preview.skippedCount ?? 0 });
  }

  async function runImport(file, skipSpecs = []) {
    if (!file) return;
    setImportingUuid(file.ingestion_file_uuid);

    // SPEC files: import ALL rows from the server-side staged table under the real vendor name.
    if (!file.mapping) {
      const { isSpec } = await getSpecInfo(file);
      if (isSpec) {
        const res = await stagedImport(file.ingestion_file_uuid, skipSpecs).catch((e) => e?.response ?? null);
        setImportingUuid(null);
        // Unresolved codes block the import (409 + issues[]): prompt to map/create/skip, then retry.
        if (res?.status === 409 && res.data?.issues?.length) { openResolve(res.data.issues, 'import', file); return; }
        if (!res || res.status >= 400) {
          notify('error', res?.data?.message || 'Failed to import file. Please try again.');
          return;
        }
        notify('success', 'Import started — it runs in the background; the status will update when it completes.');
        setRefreshData(true);
        return;
      }
    }

    let preview;
    if (file.mapping) {
      preview = file.resolvedPreview;
      if (!preview) {
        setImportingUuid(null);
        notify('error', 'Failed to import file. Please try again.');
        return;
      }
    } else {
      const raw = await getProcessedPreview(file);
      if (!raw) {
        setImportingUuid(null);
        notify('error', 'Failed to import file. Please try again.');
        return;
      }
      preview = applySkips(raw, skipSpecs);
      if (preview.issues?.length) {
        setImportingUuid(null);
        openResolve(preview.issues, 'import', file);
        return;
      }
      if (preview.dataPreview.length <= 1) {
        setImportingUuid(null);
        notify('warning', 'All rows were skipped — nothing to import.');
        return;
      }
    }

    // Capture the impact BEFORE the import (same counts as the dry run) so AI imports also save
    // metrics. Runs against a throwaway temp table; doesn't affect the real import below.
    let metrics = null;
    if (!file.mapping) {
      metrics = await dryRunImport(preview).then((r) => r.data).catch(() => null);
    }

    // ingestionFileUuid lets the server find the retained original (file-processor/<uuid>/original_*)
    // and record it on the upload-history row, so the home page widget can offer a download. Mapping
    // files carry a synthetic uuid and were uploaded to the manual/ prefix — the server handles those
    // from fileName, so don't send it.
    const res = await confirmManualUpload(
      file.mapping ? preview : { ...preview, ingestionFileUuid: file.ingestion_file_uuid }
    ).catch(() => null);
    if (!res) {
      setImportingUuid(null);
      notify('error', 'Failed to import file. Please try again.');
      return;
    }

    // AI files track lifecycle in ingestion_file (save status + metrics); legacy inline mapping files
    // do not (no row was created), so keep them visible in the list this session, marked IMPORTED,
    // instead of vanishing. (They also have a manual_file record, shown in the "Manual File Uploads" widget.)
    if (file.mapping) {
      setMappingFiles((prev) => prev.map((f) =>
        f.ingestion_file_uuid === file.ingestion_file_uuid ? { ...f, status: 'IMPORTED' } : f));
    } else {
      await markIngestionFileImported(file.ingestion_file_uuid, metrics).catch(() => null);
    }
    setImportingUuid(null);
    const skipped = preview.skippedCount ?? 0;
    notify('success', skipped
      ? `File imported successfully. ${skipped} row${skipped === 1 ? '' : 's'} skipped.`
      : 'File imported successfully.');
    setRefreshData(true);
  }

  function handleImportConfirm() {
    const file = importDialogFile;
    setImportDialogFile(null);
    runImport(file);
  }

  // The shared dialog has already persisted map/create choices; here we just
  // resume the original action with the rows the user chose to skip.
  async function onResolveApply(skipSpecs) {
    const action = resolveAction;
    const file = resolveFile;
    const ctx = resolveCtx;
    setResolveOpen(false);

    // Legacy mapping resolution (mirrors the manual-upload page): SQL codes are resolved against the
    // server-staged table by fileName; JSON codes are filtered in memory. Once clean, we build the
    // resolved preview and drop into the same dry-run/import UI as everything else.
    if (action === 'mapping') {
      setMappingBusy(true);
      try {
        let preview;
        if (ctx?.transformType === 'SQL') {
          for (const s of skipSpecs) {
            await sqlSkipRows(ctx.fileName, s.fieldName, s.value);
          }
          const res = await sqlRevalidate(ctx.fileName);
          if (res.data.issues?.length) {
            setMappingBusy(false);
            setResolveIssues(res.data.issues);
            setResolveOpen(true);
            return;
          }
          preview = {
            ...res.data,
            distributor: ctx.distributor,
            fileName: ctx.fileName,
            transformType: 'SQL',
            dataPreview: res.data.dataPreview,
          };
        } else {
          const raw = await getProcessedPreview(file);
          const filtered = applySkips(raw, skipSpecs);
          if (filtered.issues?.length) {
            setMappingBusy(false);
            setResolveCtx({ transformType: raw.transformType, fileName: raw.fileName, distributor: raw.distributor });
            setResolveIssues(filtered.issues);
            setResolveOpen(true);
            return;
          }
          preview = filtered;
        }
        addMappingFile(file, preview);
      } catch (err) {
        setMappingBusy(false);
        notify('error', 'Something went wrong resolving codes. Please try again.');
      }
      return;
    }

    if (action === 'import') runImport(file, skipSpecs);
    else handleDryRun(file, skipSpecs);
  }

  // Add a resolved mapping upload to the list so it exposes Download / Preview / Dry Run / Import,
  // the same as an AI-processed file. Status FILE PROCESSED reuses the existing row action logic.
  function addMappingFile(synth, resolvedPreview) {
    setMappingBusy(false);
    const entry = {
      ...synth,
      resolvedPreview,
      status: 'FILE PROCESSED',
      distributor_name: synth.transform,
      // ISO (UTC) so formatDttm renders it correctly in the user's zone, same as
      // server-provided rows. This is a client-only optimistic row.
      created_dttm: new Date().toISOString(),
      last_modified_dttm: new Date().toISOString(),
      isMappingFile: true, // client-only (not tracked in ingestion_file) — discard drops it locally
    };
    setMappingFiles((prev) => [entry, ...prev.filter((f) => f.ingestion_file_uuid !== entry.ingestion_file_uuid)]);
    notify('success', `"${synth.filename}" is ready — preview, dry run, or import it below.`);
  }

  // Legacy mapping upload: run the original file through the vendor's own transform (JSON or SQL),
  // resolve any unknown codes, then surface it in the list with the standard actions.
  async function startMappingFlow(name, transform, transformType) {
    const synth = {
      mapping: true,
      transform,
      transformType,
      originalFile: selectedFile,
      filename: selectedFile?.name || transform,
      ingestion_file_uuid: `mapping:${transform}:${Date.now()}`,
    };
    setFuzzyDialogOpen(false);
    handleDialogClose();
    setMappingBusy(true);

    const raw = await getProcessedPreview(synth).catch(() => null);
    if (!raw) {
      setMappingBusy(false);
      notify('error', `Could not process this file with the "${transform}" mapping. Please try again.`);
      return;
    }
    if (raw.issues?.length) {
      setMappingBusy(false);
      setResolveCtx({ transformType: raw.transformType || transformType, fileName: raw.fileName, distributor: raw.distributor });
      openResolve(raw.issues, 'mapping', synth);
      return;
    }
    addMappingFile(synth, raw);
  }

  async function handlePreview(file) {
    setPreviewFile(file);
    setPreviewData(null);
    setFeedbackText('');
    setTriageResult(null);
    // Mapping files already hold their resolved preview — reuse it (never re-run the transform, which
    // would re-stage the SQL table). AI files fetch it fresh.
    if (file.mapping) {
      const dp = file.resolvedPreview?.dataPreview;
      if (!dp?.length) {
        setPreviewFile(null);
        notify('error', 'Could not load preview. Please try again.');
        return;
      }
      setPreviewData(dp);
      return;
    }
    setPreviewLoading(true);
    // SPEC files: sample comes from the server-side staged table (staged lazily on first open).
    const { isSpec } = await getSpecInfo(file);
    const preview = isSpec
      ? await stagePreview(file.ingestion_file_uuid).then((r) => r.data).catch(() => null)
      : await getProcessedPreview(file);
    setPreviewLoading(false);
    if (!preview?.dataPreview?.length) {
      setPreviewFile(null);
      notify('error', 'Could not load preview. Please try again.');
      return;
    }
    setPreviewData(preview.dataPreview);
  }

  async function handleCancelConfirm() {
    const file = cancelDialogFile;
    setCancelDialogFile(null);
    if (!file) return;

    // Client-only mapping uploads were never persisted — just drop them from the list.
    if (file.isMappingFile) {
      setMappingFiles((prev) => prev.filter((f) => f.ingestion_file_uuid !== file.ingestion_file_uuid));
      setMessageType('success');
      setMessage('File removed.');
      handleAlertOpen();
      return;
    }

    const uuid = file.ingestion_file_uuid;
    if (!uuid) return;
    const res = await cancelIngestionFile(uuid).catch(() => null);
    if (!res) {
      setMessageType('error');
      setMessage('Failed to remove file. Please try again.');
      handleAlertOpen();
      return;
    }
    setMessageType('success');
    setMessage('File removed successfully.');
    handleAlertOpen();
    setRefreshData(true);
  }

  // Reveal a file's real cost + model — HIDDEN users only (the endpoint is hidden-gated; customers
  // only ever see points). Triggered by the "Show cost & model" button.
  async function loadCost(file) {
    const uuid = file?.ingestion_file_uuid;
    if (!uuid || !isHidden || file.isMappingFile || costByUuid[uuid] || costLoadingUuid === uuid) return;
    setCostLoadingUuid(uuid);
    const res = await getFileProcessingCost(uuid).catch(() => null);
    setCostByUuid((prev) => ({ ...prev, [uuid]: res?.data ?? { runs: [], totals: null } }));
    setCostLoadingUuid(null);
  }

  // Re-price a file's stored cost rows against the live AWS price (no re-run of the model).
  async function handleReprice(file) {
    const uuid = file?.ingestion_file_uuid;
    if (!uuid) return;
    setRepricingUuid(uuid);
    const res = await repriceFileProcessingCost(uuid).catch(() => null);
    setRepricingUuid(null);
    if (!res) {
      notify('error', 'Reprice failed. Please try again.');
      return;
    }
    const { repriced = 0, stillUnavailable = 0 } = res.data || {};
    // Refresh the now-updated cost record.
    const fresh = await getFileProcessingCost(uuid).catch(() => null);
    setCostByUuid((prev) => ({ ...prev, [uuid]: fresh?.data ?? prev[uuid] }));
    notify(
      stillUnavailable ? 'warning' : 'success',
      stillUnavailable
        ? `Repriced ${repriced}; ${stillUnavailable} still unavailable (check pricing IAM / model match).`
        : `Repriced ${repriced} cost row(s).`
    );
  }

  // ── Feedback / correction (inside the preview dialog) ──
  function closePreview() {
    setPreviewFile(null);
    setPreviewData(null);
    setFeedbackText('');
    setTriageResult(null);
    setFeedbackBusy(false);
  }

  // A file that's been processed (or whose import failed) can still be dry-run / imported. Drives the
  // Dry Run + Import buttons now surfaced from inside the Preview modal.
  function canImportFile(file) {
    const s = (file?.status ?? '').toLowerCase();
    return s === 'file processed' || s === 'import failed';
  }

  // Only AI-processed files (with a server-side processed.csv) can be corrected. Mapping/client-only
  // files and already-imported files are view-only in the preview.
  function isCorrectable(file) {
    if (!file || file.isMappingFile || file.mapping) return false;
    const s = (file.status ?? '').toLowerCase();
    return s === 'file processed' || s === 'import failed';
  }

  async function handleTriage() {
    const uuid = previewFile?.ingestion_file_uuid;
    if (!uuid || !feedbackText.trim()) return;
    setFeedbackBusy(true);
    const res = await triageFileFeedback(uuid, feedbackText.trim()).catch((e) => e?.response ?? null);
    setFeedbackBusy(false);
    if (!res || res.status >= 400) {
      notify('error', res?.data?.message || 'Could not analyze the feedback. Try rephrasing.');
      return;
    }
    setTriageResult(res.data);
  }

  async function handleApplyRemap() {
    const uuid = previewFile?.ingestion_file_uuid;
    if (!uuid || !triageResult?.transforms) return;
    setFeedbackBusy(true);
    const res = await applyFileRemap(uuid, triageResult.transforms).catch(() => null);
    if (!res) { setFeedbackBusy(false); notify('error', 'Could not apply the correction.'); return; }
    notify('success', 'Output corrected — reloading the preview.');
    setTriageResult(null);
    setFeedbackText('');
    setRefreshData(true);
    // Reload the preview in place so the user sees the corrected data.
    const fresh = await getProcessedPreview(previewFile).catch(() => null);
    setFeedbackBusy(false);
    if (fresh?.dataPreview?.length) setPreviewData(fresh.dataPreview);
  }

  async function handleReprocess() {
    const uuid = previewFile?.ingestion_file_uuid;
    if (!uuid || !feedbackText.trim()) return;
    setFeedbackBusy(true);
    const res = await reprocessFileWithFeedback(uuid, feedbackText.trim()).catch((e) => e?.response ?? null);
    setFeedbackBusy(false);
    if (res?.status === 402) {
      if (res.data?.meter) setCreditMeter(res.data.meter);
      notify('warning', res.data?.message || 'Monthly AI credit limit reached.');
      return;
    }
    if (!res || res.status >= 400) { notify('error', res?.data?.message || 'Could not start reprocessing.'); return; }
    notify('success', 'Reprocessing started — it runs in the background and uses credits.');
    closePreview();
    setRefreshData(true);
  }

  // Plain re-run from the row action — no feedback. The server rebuilds the AI reference context from
  // the current database, so a file processed under older context picks up the newer one.
  async function handleReprocessConfirm() {
    const file = reprocessDialogFile;
    setReprocessDialogFile(null);
    const uuid = file?.ingestion_file_uuid;
    if (!uuid) return;

    setReprocessingUuid(uuid);
    const res = await reprocessIngestionFile(uuid).catch((e) => e?.response ?? null);
    setReprocessingUuid(null);

    if (res?.status === 402) {
      if (res.data?.meter) setCreditMeter(res.data.meter);
      notify('warning', res.data?.message || 'Monthly AI credit limit reached.');
      return;
    }
    if (!res || res.status >= 400) {
      notify('error', res?.data?.message || 'Could not start reprocessing.');
      return;
    }
    notify('success', 'Reprocessing started — it runs in the background and uses credits.');
    setRefreshData(true);
  }

  function handleDialogClose() {
    setDialogOpen(false);
    setSelectedFile(null);
    setSelectedDistributor('');
    setMappingHint(null);
    setMappingHintLoading(false);
    setAiConfirmName(null);
  }

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    multiple: false,
    // Types the file-processor can ingest: tabular (csv/xls/xlsx) + multimodal (pdf + images).
    // Each extension is listed so the OS picker matches by name even when it reports an
    // unexpected MIME (e.g. macOS tagging .csv as application/vnd.ms-excel), which otherwise
    // greys the file out.
    accept: {
      'application/pdf': ['.pdf'],
      'text/csv': ['.csv'],
      'application/vnd.ms-excel': ['.xls', '.csv'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'image/png': ['.png'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/webp': ['.webp'],
    },
    onDrop: (accepted) => { if (accepted[0]) setSelectedFile(accepted[0]); },
  });

  async function submitWithName(name) {
    setIsUploading(true);

    // Pre-flight: let the server decide the engine.
    //  - JSON/SQL mapping -> legacy inline engine on the server (synchronous startMappingFlow).
    //  - SPEC mapping     -> the standalone processor (async queue, like AI) — the spec is applied
    //                        there; fall through to addFileToQueue (the server does NOT 409 SPEC).
    //  - no mapping (AI)  -> async processor queue.
    // The server also hard-guards JSON/SQL with a 409, handled below as a fallback.
    const decision = await getRouteDecision(name, selectedFile?.name)
      .then((r) => r.data)
      .catch(() => null);

    if (decision?.engine === 'mapping' && decision.transformType !== 'SPEC') {
      setIsUploading(false);
      startMappingFlow(name, decision.transform, decision.transformType);
      return;
    }

    const formData = new FormData();
    formData.append('dataFile', selectedFile);

    const distributorUUID = distributorList?.find(d => d.distributor_name === name)?.distributor_uuid;

    // Instrumentation: send what detection suggested alongside what is actually being submitted,
    // so the server can measure detection accuracy per vendor (the data that decides whether the
    // vendor question can eventually disappear).
    const detectedGuess = vendorDetect?.guess || vendorDetect?.topCandidate || null;
    const detected = detectedGuess
      ? { detectedVendor: detectedGuess.distributorName, detectedTier: vendorDetect.tier, detectedConfidence: detectedGuess.confidence }
      : undefined;
    // Multi-tab workbooks: tell the processor which tab the user picked ('' = all tabs, the default).
    const sheetName = sheetOptions.length > 1 && selectedSheet ? selectedSheet : undefined;
    const res = await addFileToQueue(formData, name, distributorUUID ?? '', selectedFile?.name, detected, sheetName).catch((err) => err?.response ?? null);
    setIsUploading(false);
    // 409 = the server guard caught a mapped vendor the pre-flight missed; run the mapping flow.
    if (res?.status === 409 && res?.data?.engine === 'mapping') {
      startMappingFlow(name, res.data.transform, res.data.transformType);
      return;
    }
    // 402 = monthly AI credit limit reached; the file was not queued.
    if (res?.status === 402) {
      if (res.data?.meter) setCreditMeter(res.data.meter);
      setMessageType('warning');
      setMessage(res.data?.message || 'Monthly AI credit limit reached. New AI files are paused until your credits reset.');
      handleAlertOpen();
      setFuzzyDialogOpen(false);
      handleDialogClose();
      return;
    }
    if (!res || res.status >= 400) {
      setMessageType('error');
      setMessage('Failed to upload file. Please try again.');
      handleAlertOpen();
      setFuzzyDialogOpen(false);
      handleDialogClose();
      return;
    }
    setMessageType('success');
    setMessage('File uploaded successfully.');
    handleAlertOpen();
    setRefreshData(true);
    setFuzzyDialogOpen(false);
    handleDialogClose();
  }

  // Import the file under the mapping we detected instead of the vendor the user typed. submitWithName
  // pre-flights the name, so this lands in the mapping flow (or the SPEC queue) exactly as if they
  // had picked it from the dropdown themselves.
  function useSuggestedMapping() {
    const name = mappingHint?.transform;
    if (!name) return;
    setAiConfirmName(null);
    setSelectedDistributor(name);
    setMappingHint(null);
    submitWithName(name);
  }

  function handleSubmitClick() {
    // The file structurally matches a mapping and is about to go to AI regardless. Make that an
    // explicit choice: AI matches on a different key than the mapping would, so what comes back
    // lands as new products rather than updates to the ones already in the catalog.
    if (showMappingNudge) {
      setAiConfirmName(selectedDistributor);
      return;
    }
    proceedWithVendorChecks();
  }

  function proceedWithVendorChecks() {
    const exactMatch = distributorList?.find(d => d.distributor_name === selectedDistributor);
    if (exactMatch) {
      submitWithName(selectedDistributor);
      return;
    }

    const typedLower = selectedDistributor.toLowerCase();
    const strippedOfSpecialChars = typedLower.replace(/[\s'_-]/g, '');
    const closeMatches = [];

    const substringMatches = distributorList?.filter(d => {
      const nameLower = d.distributor_name.toLowerCase();
      return typedLower.includes(nameLower) || nameLower.includes(typedLower) || strippedOfSpecialChars.includes(nameLower) || nameLower.includes(strippedOfSpecialChars);
    })?.map(d => d.distributor_name);

    if (substringMatches?.length){
      closeMatches.push(...substringMatches);
    }

    const fuseMatches = fuseRef.current?.search(selectedDistributor)?.filter(r => r.item.distributor_name !== selectedDistributor)?.map(r => r.item.distributor_name);
    const seen = new Set(substringMatches);
    const deduppedMatches = fuseMatches?.filter(n => !seen?.has(n));

    if (deduppedMatches?.length) {
      closeMatches.push(...deduppedMatches);
    }

    if (closeMatches.length === 0) {
      submitWithName(selectedDistributor);
    } else {
      setFuzzyMatches(closeMatches);
      setFuzzyDialogOpen(true);
    }
  }

  // Mapping (legacy-engine) files are client-only and shown first, above the AI-tracked files.
  const combinedFiles = [...mappingFiles, ...(fileList ?? [])];

  // A file is "finished" once it has been imported or cancelled — there is nothing left to do with
  // it. Everything else is either still moving (queued/processing/importing), waiting on a decision
  // (file processed, import failed) or broken (processing failed).
  const isFinished = (status) => {
    const s = (status ?? '').toLowerCase();
    return s === 'imported' || s === 'cancelled';
  };

  // Stored DATETIMEs arrive naive in the instance's storage zone, while client-only mapping rows are
  // ISO/UTC. dttmToEpoch resolves both to a true instant, which plain Date.parse cannot — it would
  // read a naive value as browser-local and put every row hours off for anyone outside that zone.
  const tsOf = (value) => {
    const t = dttmToEpoch(value);
    return Number.isNaN(t) ? 0 : t;
  };

  // Two views instead of five. The old split hid a file the moment you uploaded it: it left "needs
  // action" for "in progress", then came back somewhere else again when it finished or failed, so
  // following one file meant clicking between three chips. Anything still in flight, waiting on a
  // decision, or failed now lives in one list — plus whatever finished in the last day, so a file
  // you just imported or cancelled doesn't disappear the instant it lands.
  const RECENT_FINISHED_MS = 24 * 60 * 60 * 1000;
  const inActiveView = (file) =>
    !isFinished(file.status) || Date.now() - tsOf(file.last_modified_dttm) < RECENT_FINISHED_MS;

  // The two views partition the list rather than overlapping, so the counts add up to the total and
  // a file is only ever in one place — "All" repeating everything Active already showed made the
  // second view read as a superset you had to re-scan rather than somewhere else to look.
  const isHistory = statusFilter === 'history';
  const STATUS_FILTERS = [
    { key: 'active', label: 'Active', count: combinedFiles.filter(inActiveView).length },
    { key: 'history', label: 'History', count: combinedFiles.filter((f) => !inActiveView(f)).length },
  ];

  const search = searchText.trim().toLowerCase();
  const visibleFiles = combinedFiles
    .filter((f) => (isHistory ? !inActiveView(f) : inActiveView(f)))
    .filter((f) => !search || (f.filename ?? '').toLowerCase().includes(search))
    // Direction follows what each view is for. Active is a work queue: oldest first, so the file
    // that has been waiting longest is the one you see, and nothing ages out of sight at the bottom.
    // History is a record: newest first, so the most recent imports are what you land on.
    .sort((a, b) => (isHistory
      ? tsOf(b.last_modified_dttm) - tsOf(a.last_modified_dttm)
      : tsOf(a.last_modified_dttm) - tsOf(b.last_modified_dttm)));
  const pagedFiles = visibleFiles.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const onFilterChange = (key) => { setStatusFilter(key); setPage(0); };

  return (
    <div style={{padding: "30px 20px"}}>
      <Paper style={{padding: "20px"}}>
        <div style={{position: "relative", padding: "15px"}}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap'}}>
                <Typography variant="h4">File List</Typography>
                {creditMeter && creditMeter.allotmentPoints > 0 ? (() => {
                  const pct = Math.min(100, creditMeter.percentUsed || 0);
                  // Quiet by default (neutral blue-grey); only gains color when credits run low.
                  const low = creditMeter.overLimit || pct >= 80;
                  const barColor = creditMeter.overLimit ? '#f44336' : pct >= 80 ? '#b26a00' : '#90a4ae';
                  const reset = creditMeter.resetDate ? new Date(creditMeter.resetDate) : null;
                  const daysLeft = reset ? Math.max(0, Math.ceil((reset.getTime() - Date.now()) / (1000 * 60 * 60 * 24))) : null;
                  return (
                    <div style={{flex: '0 0 auto', width: 240}}>
                      <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 3}}>
                        <Typography variant="caption" style={{color: 'rgba(0,0,0,0.45)', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 10}}>Credits</Typography>
                        <Typography variant="caption" style={{color: low ? barColor : 'rgba(0,0,0,0.6)', fontWeight: 600}}>
                          {Number(creditMeter.remainingPoints).toLocaleString()} left
                        </Typography>
                      </div>
                      <div style={{height: 5, borderRadius: 3, background: '#eee', overflow: 'hidden'}}>
                        <div style={{width: `${pct}%`, height: '100%', background: barColor, transition: 'width .3s'}} />
                      </div>
                      {/* Only surface the reset once credits are running low (>=80% used). */}
                      {low && (
                        <Typography variant="caption" style={{color: creditMeter.overLimit ? '#f44336' : '#b26a00', display: 'block', marginTop: 2, fontSize: 11, whiteSpace: 'nowrap'}}>
                          {creditMeter.overLimit ? 'limit reached · ' : ''}
                          {daysLeft != null && reset ? `resets in ${daysLeft}d (${reset.toLocaleDateString()})` : ''}
                        </Typography>
                      )}
                    </div>
                  );
                })() : null}
              </div>
            </Grid>
            <Grid item xs={12}>
              <Button variant="contained" onClick={() => setDialogOpen(true)}>Import New File</Button>
              <Button variant="outlined" style={{ marginLeft: 8 }} onClick={() => setBulkOpen(true)}>
                Bulk Upload
              </Button>
            </Grid>
            {combinedFiles.length ? (
              <Grid item xs={12}>
                <div style={{display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap'}}>
                  {STATUS_FILTERS.map((f) => (
                    <Chip
                      key={f.key}
                      label={`${f.label} (${f.count})`}
                      size="small"
                      clickable
                      onClick={() => onFilterChange(f.key)}
                      variant={statusFilter === f.key ? 'default' : 'outlined'}
                      style={statusFilter === f.key
                        ? {backgroundColor: '#142e3e', color: '#fff'}
                        : {}}
                    />
                  ))}
                  <div style={{flex: 1}} />
                  <TextField
                    size="small"
                    variant="outlined"
                    placeholder="Search filename…"
                    value={searchText}
                    onChange={(e) => { setSearchText(e.target.value); setPage(0); }}
                    style={{minWidth: '220px'}}
                  />
                </div>
              </Grid>
            ) : null}
            <Grid item xs={12}>
              {combinedFiles.length ? (
                visibleFiles.length ? (
                <Grid container spacing={1}>
                  {pagedFiles.map((file) => {
                    const status = (file.status ?? '').toLowerCase();
                    const statusColor = status.includes('fail') || status === 'cancelled'
                      ? '#f44336'
                      : status === 'imported'
                      ? '#1565c0'
                      : status === 'importing'
                      ? '#b26a00'
                      : status.includes('processed')
                      ? '#388E3C'
                      : 'inherit';
                    const isImporting = status === 'importing';
                    // "file processed" is ready to import; "import failed" can be retried.
                    const isProcessed = status === 'file processed' || status === 'import failed';
                    const isImported = status === 'imported';
                    // Queued/failed-to-process rows can be cancelled; a reviewed-but-unwanted processed
                    // file can be discarded (same action, friendlier label).
                    const isCancellable = status === 'waiting in queue' || status === 'processing failed';
                    const isDiscardable = isProcessed;
                    const hasError = !!file.last_run_error;
                    // Anything that stopped short of being imported can be re-run against the current AI
                    // context. Mapping files are client-only (no ingestion_file row) so they can't be.
                    const isReprocessable =
                      !file.isMappingFile && (isProcessed || status === 'processing failed');

                    return (
                      <Grid item xs={12} key={file.ingestion_file_uuid}>
                        <Accordion variant="outlined" style={{backgroundColor: '#f5f5f5'}}>
                          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <div style={{display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', flex: 1, marginRight: '8px'}}>
                              <div style={{flex: '1 1 200px', minWidth: 0}}>
                                <Typography variant="body2" style={{fontWeight: 'bold', wordBreak: 'break-all'}}>
                                  {file.filename}
                                </Typography>
                                <Typography variant="caption" style={{color: 'rgba(0,0,0,0.5)', wordBreak: 'break-all'}}>
                                  {[file.distributor_name, formatDttm(file.last_modified_dttm, { fallback: '' })].filter(Boolean).join(' · ')}
                                </Typography>
                              </div>
                              <Typography variant="caption" style={{color: statusColor, fontWeight: 'bold', whiteSpace: 'nowrap'}}>{statusLabel(file.status)}</Typography>
                              {isImporting && <CircularProgress size={14} style={{color: '#b26a00'}} />}
                              {/* Collapsed row keeps only Preview + Discard. Download/Reprocess live in the
                                  expanded view; Dry Run/Import are reached through the Preview modal. */}
                              {(isProcessed || isImported) && (
                                <Button
                                  size="small"
                                  variant="contained"
                                  style={{whiteSpace: 'nowrap', backgroundColor: '#fff'}}
                                  onClick={(e) => { e.stopPropagation(); handlePreview(file); }}
                                >
                                  Preview
                                </Button>
                              )}
                              {isCancellable && (
                                <Button
                                  size="small"
                                  variant="contained"
                                  style={{whiteSpace: 'nowrap', backgroundColor: '#f44336', color: '#fff'}}
                                  onClick={(e) => { e.stopPropagation(); setCancelDialogFile(file); }}
                                >
                                  Cancel
                                </Button>
                              )}
                              {isDiscardable && (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  style={{whiteSpace: 'nowrap', borderColor: '#f44336', color: '#f44336'}}
                                  onClick={(e) => { e.stopPropagation(); setCancelDialogFile(file); }}
                                >
                                  Discard
                                </Button>
                              )}
                            </div>
                          </AccordionSummary>
                          <AccordionDetails style={{flexDirection: 'column', gap: '8px', backgroundColor: '#fff', borderRadius: '0 0 4px 4px', overflow: 'hidden'}}>
                            {/* Secondary actions, moved off the collapsed row. */}
                            {(isProcessed || isImported || isReprocessable) && (
                              <div style={{display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 4}}>
                                {(isProcessed || isImported) && (
                                  <Button
                                    size="small"
                                    variant="contained"
                                    style={{whiteSpace: 'nowrap', backgroundColor: '#fff'}}
                                    onClick={(e) => { e.stopPropagation(); handleDownload(file); }}
                                  >
                                    Download
                                  </Button>
                                )}
                                {/* Mapping files are client-only — there's no retained upload to fetch. */}
                                {(isProcessed || isImported) && !file.mapping && !file.isMappingFile && (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    style={{whiteSpace: 'nowrap'}}
                                    title="The file that was uploaded, in its original format"
                                    onClick={(e) => { e.stopPropagation(); handleDownloadOriginal(file); }}
                                  >
                                    Download Original
                                  </Button>
                                )}
                                {isReprocessable && (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    disabled={reprocessingUuid === file.ingestion_file_uuid}
                                    style={{whiteSpace: 'nowrap', borderColor: '#b26a00', color: '#b26a00'}}
                                    onClick={(e) => { e.stopPropagation(); setReprocessDialogFile(file); }}
                                  >
                                    {reprocessingUuid === file.ingestion_file_uuid
                                      ? <CircularProgress size={16} style={{color: '#b26a00'}} />
                                      : 'Reprocess'}
                                  </Button>
                                )}
                              </div>
                            )}
                            <Typography variant="caption">
                              <span style={{color: 'rgba(0,0,0,0.54)'}}>Vendor: </span>
                              {file.distributor_name}
                            </Typography>
                            {file.distributor_uuid && (
                              <FormControlLabel
                                style={{marginLeft: 0, marginRight: 0}}
                                onClick={(e) => e.stopPropagation()}
                                control={
                                  <Switch
                                    size="small"
                                    color="primary"
                                    checked={Number((distributorList ?? []).find((d) => d.distributor_uuid === file.distributor_uuid)?.auto_import_enabled) === 1}
                                    onChange={(e) => handleAutoImportToggle(file, e.target.checked)}
                                  />
                                }
                                label={
                                  <Typography variant="caption">
                                    Auto-import clean files from this vendor
                                    <span style={{color: 'rgba(0,0,0,0.54)'}}> (skipped if anything needs review — codes, missing identity, probable duplicates)</span>
                                  </Typography>
                                }
                              />
                            )}
                            <Typography variant="caption">
                              <span style={{color: 'rgba(0,0,0,0.54)'}}>Created: </span>
                              {formatDttm(file.created_dttm)}
                            </Typography>
                            <Typography variant="caption">
                              <span style={{color: 'rgba(0,0,0,0.54)'}}>Last Modified: </span>
                              {formatDttm(file.last_modified_dttm)}
                            </Typography>
                            {(() => {
                              const m = parseMetrics(file.import_metrics);
                              if (!m) return null;
                              return (
                                <div style={{marginTop: 4}}>
                                  <Typography variant="caption" style={{color: 'rgba(0,0,0,0.54)', display: 'block', fontWeight: 'bold'}}>Import impact</Typography>
                                  {METRIC_LABELS.filter(([k]) => m[k] != null).map(([k, label]) => (
                                    <Typography key={k} variant="caption" style={{display: 'block'}}>
                                      <span style={{color: 'rgba(0,0,0,0.54)'}}>{label}: </span>
                                      <span style={{fontWeight: 'bold', color: k === 'removedPrices' && m[k] > 0 ? '#f44336' : 'inherit'}}>{Number(m[k]).toLocaleString()}</span>
                                    </Typography>
                                  ))}
                                </div>
                              );
                            })()}
                            {/* Processing results (from ingestion_file.summary_json, written at FILE PROCESSED):
                                rows the AI validation excluded + skus it had to generate. AI-loop internals
                                (pilot/tiebreak) stay hidden-only, like tokens/model. */}
                            {(() => {
                              const s = parseMetrics(file.summary_json);
                              if (!s) return null;
                              const rejected = Number(s.rejectedRows) || 0;
                              const generated = Number(s.generatedSkus) || 0;
                              const catalogAdopted = (Number(s.catalogAdopted) || 0) + (Number(s.adoptedProducts) || 0);
                              const autoImport = s.autoImport;
                              const pilot = Number(s.pilotIterations) || 0;
                              const tiebreaks = Number(s.tiebreakPairs) || 0;
                              // Pilot verdicts other than approve/skipped mean the AI reviewer delivered
                              // the file for human review with open concerns.
                              const pilotHeld = s.pilotOutcome && s.pilotOutcome !== 'approve' && s.pilotOutcome !== 'skipped';
                              if (!rejected && !generated && !catalogAdopted && !autoImport && !pilotHeld && !(isHidden && (pilot || tiebreaks))) return null;
                              return (
                                <div style={{marginTop: 4}}>
                                  <Typography variant="caption" style={{color: 'rgba(0,0,0,0.54)', display: 'block', fontWeight: 'bold'}}>Processing results</Typography>
                                  {pilotHeld && (
                                    <Typography variant="caption" style={{display: 'block', color: '#b26a00'}}>
                                      The AI reviewer didn't fully sign off on this file{s.pilotOpenIssues ? ` — ${s.pilotOpenIssues}` : ''}. Check the preview / dry run before importing, or Reprocess to run it through the AI again (it picks up everything learned so far).
                                    </Typography>
                                  )}
                                  {autoImport?.result === 'imported' && (
                                    <Typography variant="caption" style={{display: 'block', fontWeight: 'bold'}}>
                                      Imported automatically — every safety check passed.
                                    </Typography>
                                  )}
                                  {autoImport?.result === 'skipped' && (
                                    <Typography variant="caption" style={{display: 'block', color: '#b26a00'}}>
                                      Auto-import held for review: {(autoImport.reasons || []).join('; ') || 'see details'}.
                                    </Typography>
                                  )}
                                  {catalogAdopted > 0 && (
                                    <Typography variant="caption" style={{display: 'block'}} color="textSecondary">
                                      {catalogAdopted.toLocaleString()} product{catalogAdopted === 1 ? '' : 's'} matched to your existing catalog during processing (no duplicates created).
                                    </Typography>
                                  )}
                                  {rejected > 0 && (
                                    <Typography variant="caption" style={{display: 'block', color: '#f44336'}}>
                                      {rejected.toLocaleString()} row{rejected === 1 ? '' : 's'} failed validation and {rejected === 1 ? 'was' : 'were'} left out of the processed file
                                      {' — '}
                                      <span
                                        style={{textDecoration: 'underline', cursor: 'pointer'}}
                                        onClick={(e) => { e.stopPropagation(); handleDownloadRejected(file); }}
                                      >
                                        download them with reasons
                                      </span>.
                                    </Typography>
                                  )}
                                  {generated > 0 && (
                                    <Typography variant="caption" style={{display: 'block'}} color="textSecondary">
                                      {generated.toLocaleString()} row{generated === 1 ? ' had' : 's had'} no SKU — one was filled in from the color or style.
                                    </Typography>
                                  )}
                                  {isHidden && pilot > 0 && (
                                    <Typography variant="caption" style={{display: 'block'}}>
                                      <span style={{color: 'rgba(0,0,0,0.54)'}}>AI review iterations: </span>
                                      <span style={{fontWeight: 'bold'}}>{pilot}</span>
                                    </Typography>
                                  )}
                                  {isHidden && tiebreaks > 0 && (
                                    <Typography variant="caption" style={{display: 'block'}}>
                                      <span style={{color: 'rgba(0,0,0,0.54)'}}>Match tiebreaks (matched / asked): </span>
                                      <span style={{fontWeight: 'bold'}}>{Number(s.tiebreakMatched) || 0} / {tiebreaks}</span>
                                    </Typography>
                                  )}
                                </div>
                              );
                            })()}
                            {isHidden && (
                              <Typography variant="caption">
                                <span style={{color: 'rgba(0,0,0,0.54)'}}>Processing Time (Latest / Total): </span>
                                {formatDuration(file.latest_processing_time)} / {formatDuration(file.total_processing_time)}
                              </Typography>
                            )}
                            {isHidden && hasError && (
                              <Typography variant="caption" style={{color: '#f44336', wordBreak: 'break-word'}}>
                                <span style={{color: 'rgba(0,0,0,0.54)'}}>Error: </span>
                                {file.last_run_error}
                              </Typography>
                            )}
                            {/* Customer-facing: points only (never cost/model/tokens). */}
                            {!file.isMappingFile && file.points != null && Number(file.points) > 0 && (
                              <Typography variant="caption">
                                <span style={{color: 'rgba(0,0,0,0.54)'}}>Credits used: </span>
                                <span style={{fontWeight: 'bold'}}>{Number(file.points).toLocaleString()} pts</span>
                              </Typography>
                            )}
                            {/* Hidden-only reveal: real cost, model, tokens — behind a button so it's never on-screen by default. */}
                            {isHidden && !file.isMappingFile && (() => {
                              const uuid = file.ingestion_file_uuid;
                              const cost = costByUuid[uuid];
                              if (!cost) {
                                return (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    disabled={costLoadingUuid === uuid}
                                    onClick={(e) => { e.stopPropagation(); loadCost(file); }}
                                    style={{whiteSpace: 'nowrap', alignSelf: 'flex-start', marginTop: 4}}
                                  >
                                    {costLoadingUuid === uuid ? <CircularProgress size={14} /> : 'Show cost & model'}
                                  </Button>
                                );
                              }
                              if (!cost.runs || !cost.runs.length) {
                                return <Typography variant="caption" style={{color: 'rgba(0,0,0,0.54)'}}>No cost recorded for this file.</Typography>;
                              }
                              const t = cost.totals || {};
                              const model = (cost.runs.find((r) => r.model) || {}).model;
                              // 'published-fallback' rows carry a real (published) rate — treat as priced.
                              const pending = cost.runs.filter((r) => r.price_source !== 'aws-price-list' && r.price_source !== 'published-fallback');
                              const awaitingSplit = pending.some((r) => r.price_source === 'awaiting-split');
                              return (
                                <div style={{marginTop: 4, paddingTop: 4, borderTop: '1px dashed rgba(0,0,0,0.12)'}}>
                                  <Typography variant="caption" style={{color: 'rgba(0,0,0,0.54)', display: 'block', fontWeight: 'bold'}}>Internal — cost &amp; model</Typography>
                                  <Typography variant="caption" style={{display: 'block'}}>
                                    <span style={{color: 'rgba(0,0,0,0.54)'}}>Cost: </span>
                                    <span style={{fontWeight: 'bold'}}>${(Number(t.costUsd) || 0).toFixed(4)}</span>
                                    <span style={{color: 'rgba(0,0,0,0.54)'}}> = {Number(t.points || 0).toLocaleString()} pts</span>
                                    {cost.runs.length > 1 && <span style={{color: 'rgba(0,0,0,0.54)'}}> ({cost.runs.length} runs)</span>}
                                  </Typography>
                                  {model && (
                                    <Typography variant="caption" style={{display: 'block', wordBreak: 'break-all'}}>
                                      <span style={{color: 'rgba(0,0,0,0.54)'}}>Model: </span>{model}
                                    </Typography>
                                  )}
                                  <Typography variant="caption" style={{display: 'block'}}>
                                    <span style={{color: 'rgba(0,0,0,0.54)'}}>Tokens (in / out): </span>
                                    {Number(t.inputTokens || 0).toLocaleString()} / {Number(t.outputTokens || 0).toLocaleString()}
                                  </Typography>
                                  {pending.length > 0 && (
                                    <div style={{marginTop: 4, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap'}}>
                                      <Typography variant="caption" style={{color: '#b26a00'}}>
                                        {awaitingSplit
                                          ? 'Not priceable — no token split (re-run to price).'
                                          : `${pending.length} run(s) not yet priced.`}
                                      </Typography>
                                      {!awaitingSplit && (
                                        <Button
                                          size="small"
                                          variant="outlined"
                                          disabled={repricingUuid === uuid}
                                          onClick={(e) => { e.stopPropagation(); handleReprice(file); }}
                                          style={{whiteSpace: 'nowrap'}}
                                        >
                                          {repricingUuid === uuid ? <CircularProgress size={14} /> : 'Reprice'}
                                        </Button>
                                      )}
                                    </div>
                                  )}
                                </div>
                              );
                            })()}
                          </AccordionDetails>
                        </Accordion>
                      </Grid>
                    );
                  })}
                  <Grid item xs={12}>
                    <TablePagination
                      component="div"
                      count={visibleFiles.length}
                      page={page}
                      rowsPerPage={rowsPerPage}
                      rowsPerPageOptions={[10, 25, 50]}
                      size="small"
                      onPageChange={(e, newPage) => setPage(newPage)}
                      onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                    />
                  </Grid>
                </Grid>
                ) : (
                  <Typography style={{color: 'rgba(0,0,0,0.54)'}}>No files match this filter.</Typography>
                )
              ) : (
                <Typography>No files have been found</Typography>
              )}
            </Grid>
          </Grid>
        </div>
      </Paper>

      <BulkUpload
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        distributorList={distributorList}
        onUploaded={() => setRefreshData(true)}
        onNotify={(type, text) => { setMessageType(type); setMessage(text); handleAlertOpen(); }}
      />

      <Dialog open={dialogOpen} onClose={handleDialogClose} fullWidth maxWidth="sm">
        <DialogTitle>Import New File</DialogTitle>
        <DialogContent>
          <Grid container spacing={3}>
            {houseErp && !showMappingNudge && (
              <Grid item xs={12}>
                <Alert severity="info" style={{ marginBottom: -8 }}>
                  This site imports most price files through <strong>{houseErp.transform}</strong>
                  {houseErp.fileCount > 1 ? ` (${houseErp.fileCount} files)` : ''}. If this file came out of
                  your system, pick <strong>{houseErp.transform}</strong> above rather than the vendor's
                  name — that keeps prices matched to the products you already have.
                </Alert>
              </Grid>
            )}
            <Grid item xs={12}>
              <Autocomplete
                freeSolo
                options={distributorList ?? []}
                // Options are pre-sorted mappings-first, which is what makes these groups contiguous.
                groupBy={(option) => (option.transformType ? 'Your file specs' : 'Vendors (AI processing)')}
                getOptionLabel={(option) => (typeof option === 'string' ? option : (option.distributor_name || ''))}
                renderOption={(option) => {
                  const tt = option.transformType;
                  const chip = tt === 'SPEC'
                    ? { label: 'Rule mapping', bg: '#e3f2fd', fg: '#1565c0' }
                    : tt === 'SQL'
                    ? { label: 'SQL transform', bg: '#fff3e0', fg: '#b26a00' }
                    : tt === 'JSON'
                    ? { label: 'JSON transform', bg: '#fff3e0', fg: '#b26a00' }
                    : tt
                    ? { label: 'Transform', bg: '#fff3e0', fg: '#b26a00' }
                    : { label: 'Vendor (AI)', bg: '#eeeeee', fg: '#555' };
                  return (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: 8 }}>
                      <span style={{ wordBreak: 'break-word' }}>{option.distributor_name}</span>
                      <Chip size="small" label={chip.label} style={{ backgroundColor: chip.bg, color: chip.fg, height: 20 }} />
                    </div>
                  );
                }}
                inputValue={selectedDistributor}
                onInputChange={(e, value) => setSelectedDistributor(value)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Vendor"
                    error={isDistributorNameValErr}
                    variant="outlined"
                    fullWidth
                  />
                )}
              />
            </Grid>
            {/* Vendor detection (same engine as the bulk drop): evidence for the pre-fill, a
                "did you mean" hint for weak matches, and a nudge when the user's pick disagrees
                with a confident detection. Never blocks — the user's choice always wins. */}
            {selectedFile && vendorDetectLoading && (
              <Grid item xs={12} style={{ paddingTop: 0 }}>
                <Typography variant="caption" color="textSecondary">Detecting vendor…</Typography>
              </Grid>
            )}
            {(() => {
              if (!selectedFile || vendorDetectLoading || !vendorDetect) return null;
              const normVendor = (s) => String(s ?? '').toLowerCase().replace(/[^a-z0-9]+/g, '');
              const guess = vendorDetect.guess;
              const hint = !guess && vendorDetect.topCandidate;
              if (guess && selectedDistributor && normVendor(selectedDistributor) !== normVendor(guess.distributorName)) {
                return (
                  <Grid item xs={12} style={{ paddingTop: 0 }}>
                    <Alert
                      severity="warning"
                      action={
                        <Button size="small" style={{ whiteSpace: 'nowrap' }} onClick={() => setSelectedDistributor(guess.distributorName)}>
                          Use {guess.distributorName}
                        </Button>
                      }
                    >
                      This file looks like it's from <strong>{guess.distributorName}</strong> — {guess.reasons?.[0] || 'multiple signals agree'}. Importing it under “{selectedDistributor}” would file these prices against the wrong vendor.
                    </Alert>
                  </Grid>
                );
              }
              if (guess) {
                return (
                  <Grid item xs={12} style={{ paddingTop: 0 }}>
                    <Typography variant="caption" color="textSecondary">
                      Detected vendor: <strong>{guess.distributorName}</strong>{guess.isNew ? ' (new vendor)' : ''} — {guess.reasons?.[0] || ''}
                    </Typography>
                  </Grid>
                );
              }
              if (hint && !selectedDistributor) {
                return (
                  <Grid item xs={12} style={{ paddingTop: 0 }}>
                    <Typography variant="caption" color="textSecondary">
                      Could not confidently detect the vendor. Best guess: {hint.distributorName} — {hint.reasons?.[0] || ''}
                    </Typography>
                  </Grid>
                );
              }
              return null;
            })()}
            <Grid item xs={12}>
              <div
                {...getRootProps()}
                style={{
                  border: `2px dashed ${isDragActive ? '#142e3e' : '#aaa'}`,
                  borderRadius: 4,
                  padding: '24px 16px',
                  textAlign: 'center',
                  cursor: 'pointer',
                  background: isDragActive ? '#e3f2fd' : 'transparent',
                }}
              >
                <input {...getInputProps()} />
                <Typography variant="body1" color={isDragActive ? 'primary' : 'textSecondary'}>
                  {selectedFile
                    ? selectedFile.name
                    : isDragActive
                    ? 'Drop file here...'
                    : 'Drag and drop a file here, or click to select. Accepted: PDF, CSV, XLS, XLSX, PNG, JPG, WEBP.'}
                </Typography>
              </div>
            </Grid>
            {sheetOptions.length > 1 && (
              <Grid item xs={12} style={{ paddingTop: 0 }}>
                <TextField
                  select
                  fullWidth
                  variant="outlined"
                  size="small"
                  label="Which tabs have the pricing?"
                  value={selectedSheet}
                  onChange={(e) => setSelectedSheet(e.target.value)}
                  helperText="All tabs is right when every tab is pricing (e.g. one per brand). Pick a single tab to skip instruction or scratch tabs."
                >
                  <MenuItem value="">All tabs ({sheetOptions.length})</MenuItem>
                  {sheetOptions.map((s) => (
                    <MenuItem key={s.name} value={s.name}>
                      {s.name} only ({s.rows}{s.capped ? '+' : ''} rows)
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}
            {mappingHintLoading && (
              <Grid item xs={12} style={{ paddingTop: 0 }}>
                <Typography variant="caption" color="textSecondary">Checking the file’s columns…</Typography>
              </Grid>
            )}
            {showMappingNudge && (
              <Grid item xs={12} style={{ paddingTop: 0 }}>
                <Alert
                  severity="warning"
                  action={
                    <Button size="small" style={{ whiteSpace: 'nowrap' }} onClick={useSuggestedMapping}>
                      Use {mappingHint.transform}
                    </Button>
                  }
                >
                  {/* The columns are the evidence, so say so — "we think" invites people to shrug it
                      off, while naming the match lets them confirm it at a glance. */}
                  {mappingIsNative ? (
                    <>
                      This file is on the <strong>Showroom Pricing</strong> template — its columns are
                      ours, and each row already names its own vendor in the distributor column. Picking{' '}
                      <strong>{selectedDistributor || 'a vendor'}</strong> sends it through AI processing
                      instead, which re-reads columns that are already where they belong and spends
                      credits doing it.
                    </>
                  ) : (
                    <>
                      This file’s columns {mappingHint.strength === 'strong' ? 'match' : 'look like'} your{' '}
                      <strong>{mappingHint.transform}</strong> file spec
                      {mappingHint.ambiguous ? ' (or another of your specs)' : ''}, but{' '}
                      <strong>{selectedDistributor || 'the vendor you picked'}</strong> has no mapping, so it
                      would go through AI processing.{' '}
                      {mappingUsesIdMatching
                        ? `AI matches on style and color rather than your ${mappingHint.transform} ids — products you already have would come back as new ones.`
                        : `${mappingHint.transform} reads these columns the same way every time and costs no AI credits.`}
                    </>
                  )}
                </Alert>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button 
            variant="contained" 
            style={{margin: "10px"}}
            onClick={handleDialogClose}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            style={{backgroundColor: "#142e3e", color: "#28c4fc", margin: "10px"}}
            disabled={!selectedFile || !selectedDistributor || isDistributorNameValErr || isUploading}
            onClick={handleSubmitClick}
          >
            {isUploading ? <CircularProgress size={20} style={{color: "#28c4fc"}} /> : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Second step before a file that matches a mapping goes to AI anyway. It stays possible — a
          one-off PDF from a vendor whose exports usually come through the ERP is a real case — but
          the cost of getting it wrong (a duplicated catalog someone has to unpick by hand) is high
          enough that it should not happen on a single click. */}
      <Dialog open={!!aiConfirmName} onClose={() => setAiConfirmName(null)} fullWidth maxWidth="sm">
        <DialogTitle>Use AI processing for this file?</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            <p>
              <strong>{selectedFile?.name}</strong>{' '}
              {mappingIsNative
                ? 'is on the Showroom Pricing template'
                : <>has the columns of your <strong>{mappingHint?.transform}</strong> file spec</>}
              , but you’ve selected the vendor <strong>{aiConfirmName}</strong>
              {mappingIsNative ? '' : ', which has no mapping'}. Importing it with AI instead:
            </p>
            <ul>
              {mappingUsesIdMatching && (
                <li>
                  matches products by style, color and size rather than your {mappingHint?.transform} ids
                  — items already in your catalog are likely to be added again as new products
                </li>
              )}
              {mappingIsFullRefresh && (
                <li>
                  skips the full price refresh your {mappingHint?.transform} import does, so prices left
                  out of this file stay live instead of being retired
                </li>
              )}
              <li>
                reads the columns by inference each run rather than by your fixed {mappingHint?.transform}{' '}
                rules, so a column it reads differently this time changes what gets imported
              </li>
              <li>uses AI credits, which the {mappingHint?.transform} import does not</li>
            </ul>
            <p>
              If this file really did come from {aiConfirmName} rather than out of your system, carry on.
            </p>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" style={{ margin: '10px' }} onClick={() => setAiConfirmName(null)}>
            Go back
          </Button>
          <Button
            variant="contained"
            style={{ backgroundColor: '#142e3e', color: '#28c4fc', margin: '10px' }}
            onClick={useSuggestedMapping}
          >
            Use {mappingHint?.transform} instead
          </Button>
          <Button
            style={{ margin: '10px' }}
            onClick={() => { setAiConfirmName(null); proceedWithVendorChecks(); }}
          >
            Import with AI anyway
          </Button>
        </DialogActions>
      </Dialog>
      {(() => {
        // A processed file is "discarded" (reviewed, not wanted); a queued/failed one is "cancelled".
        const s = (cancelDialogFile?.status ?? '').toLowerCase();
        const isDiscard = s === 'file processed' || s === 'import failed';
        return (
          <Dialog open={!!cancelDialogFile} onClose={() => setCancelDialogFile(null)} fullWidth maxWidth="sm">
            <DialogTitle>{isDiscard ? 'Discard File' : 'Cancel File'}</DialogTitle>
            <DialogContent>
              <DialogContentText>
                Are you sure you want to {isDiscard ? 'discard' : 'cancel'}{' '}
                <strong>{cancelDialogFile?.filename}</strong>?{' '}
                {isDiscard
                  ? 'It has been processed but not imported — the processed data will be removed and it won’t be imported. This cannot be undone.'
                  : 'This cannot be undone.'}
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button variant="contained" style={{margin: "10px"}} onClick={() => setCancelDialogFile(null)}>
                Go Back
              </Button>
              <Button variant="contained" style={{backgroundColor: "#f44336", color: "#fff", margin: "10px"}} onClick={handleCancelConfirm}>
                {isDiscard ? 'Discard File' : 'Cancel Processing'}
              </Button>
            </DialogActions>
          </Dialog>
        );
      })()}

      <Dialog open={!!reprocessDialogFile} onClose={() => setReprocessDialogFile(null)} fullWidth maxWidth="sm">
        <DialogTitle>Reprocess File</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Run <strong>{reprocessDialogFile?.filename}</strong> through the file processor again using the
            current AI context. This re-runs the AI and <strong>uses credits</strong>.
          </DialogContentText>
          <DialogContentText variant="body2" style={{color: '#b26a00'}}>
            Any existing processed output for this file is replaced, and a previous preview or dry run is
            discarded. Nothing already imported into your catalog is affected.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" style={{margin: "10px"}} onClick={() => setReprocessDialogFile(null)}>
            Go Back
          </Button>
          <Button
            variant="contained"
            style={{backgroundColor: "#b26a00", color: "#fff", margin: "10px"}}
            onClick={handleReprocessConfirm}
          >
            Reprocess (uses credits)
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={fuzzyDialogOpen} onClose={() => setFuzzyDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Similar Distributor Found</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Similar distributors already exist. Would you like to use one instead, or continue with what you typed?
          </DialogContentText>
          <Grid container spacing={1} alignItems="center">
            <Grid item xs={12}>
              <Typography variant="h6" style={{margin: "10px auto 5px", borderBottom: "1px solid #000000"}}>Similar Options</Typography>
            </Grid>
            {fuzzyMatches.map(name => (
              <Grid item xs={12} md={6} key={`btn-${name}`} style={{textAlign: "right"}}>
                <Button variant="outlined" fullWidth size="small" onClick={() => submitWithName(name)}>Use "{name}"</Button>
              </Grid>
            ))}
            <Grid item xs={12}>
              <Typography variant="h6" style={{margin: "20px auto 5px", borderBottom: "1px solid #000000"}}>What You Typed</Typography>
            </Grid>
            <Grid item xs={12} md={6} style={{textAlign: "right"}}>
              <Button variant="outlined" fullWidth size="small" onClick={() => submitWithName(selectedDistributor)}>Use "{selectedDistributor}"</Button>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" style={{margin: "15px"}} onClick={() => setFuzzyDialogOpen(false)}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={!!importDialogFile} onClose={() => { setImportDialogFile(null); setImportDupWarnings(null); }} fullWidth maxWidth="sm">
        <DialogTitle>Import Pricing</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Import <strong>{importDialogFile?.filename}</strong> into the catalog using the Showroom Pricing template?
          </DialogContentText>
          <DialogContentText style={{color: '#b26a00'}}>
            This updates live pricing. For every product/color in the file, all existing prices for that item and
            vendor are replaced — any price not present in the file will be removed. Run a Dry Run first to preview the impact.
          </DialogContentText>
          {importDupWarnings?.probableDuplicates > 0 && (
            <DialogContentText style={{color: '#b26a00'}}>
              The dry run flagged <strong>{importDupWarnings.probableDuplicates}</strong> probable duplicate
              product{importDupWarnings.probableDuplicates === 1 ? '' : 's'} — importing will create{' '}
              {importDupWarnings.probableDuplicates === 1 ? 'it' : 'them'} as new product{importDupWarnings.probableDuplicates === 1 ? '' : 's'} with{' '}
              {importDupWarnings.probableDuplicates === 1 ? 'its' : 'their'} own QR code{importDupWarnings.probableDuplicates === 1 ? '' : 's'}. You can merge duplicates afterwards on the Duplicates page.
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="contained" style={{margin: "10px"}} onClick={() => { setImportDialogFile(null); setImportDupWarnings(null); }}>
            Go Back
          </Button>
          <Button variant="contained" style={{backgroundColor: "#142e3e", color: "#28c4fc", margin: "10px"}} onClick={handleImportConfirm}>
            Import
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={!!dryRunFile} onClose={() => { setDryRunFile(null); setDryRunResult(null); }} fullWidth maxWidth="sm">
        <DialogTitle>Dry Run — {dryRunFile?.filename}</DialogTitle>
        <DialogContent>
          {dryRunLoading || !dryRunResult ? (
            <div style={{display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 0'}}>
              <CircularProgress size={20} />
              <Typography variant="body2">Analyzing file against existing data…</Typography>
            </div>
          ) : (
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <Typography variant="body2" color="textSecondary">
                  {dryRunResult.totalRows} row{dryRunResult.totalRows === 1 ? '' : 's'} in file
                  {dryRunResult.skippedRows > 0 ? ` (${dryRunResult.skippedRows} skipped)` : ''}
                </Typography>
              </Grid>
              {[
                ['New products', dryRunResult.newProducts],
                ['Existing products updated', dryRunResult.updatedProducts],
                // Subset of the row above: matched only because auto-adopt rewrote drifted identity.
                ['↳ of which auto-matched to your catalog', dryRunResult.adoptedProducts],
                ['Printed products updated', dryRunResult.printedProductsUpdated],
                ['New colors / SKUs', dryRunResult.newSkus],
                ['New prices', dryRunResult.newPrices],
                ['Prices updated', dryRunResult.updatedPrices],
                ['Prices removed', dryRunResult.removedPrices],
                ['Probable duplicate products', dryRunResult.duplicateWarnings?.probableDuplicates],
                ['Rows with a generated SKU', dryRunResult.generatedSkuRows],
              ].filter(([, value]) => value != null).map(([label, value]) => (
                <Grid item xs={12} key={label} style={{display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #eee', padding: '4px 0'}}>
                  <Typography variant="body2" style={label.startsWith('↳') ? {paddingLeft: 16, color: 'rgba(0,0,0,0.6)'} : undefined}>{label}</Typography>
                  <Typography variant="body2" style={{fontWeight: 'bold', color: label === 'Prices removed' && value > 0 ? '#f44336' : ((label === 'Printed products updated' || label === 'Probable duplicate products') && value > 0 ? '#b26a00' : 'inherit')}}>{value}</Typography>
                </Grid>
              ))}
              {dryRunResult.printedProductsUpdated > 0 && (
                <Grid item xs={12}>
                  <Typography variant="caption" style={{color: '#b26a00'}}>
                    {dryRunResult.printedProductsUpdated} product{dryRunResult.printedProductsUpdated === 1 ? ' is' : 's are'} currently printed — those price changes reach the showroom floor on the next label print.
                  </Typography>
                </Grid>
              )}
              {dryRunResult.unaffectedProducts > 0 && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="textSecondary">
                    {dryRunResult.unaffectedProducts} other product{dryRunResult.unaffectedProducts === 1 ? ' from' : 's from'} {dryRunResult.unaffectedManufacturers === 1 ? 'the manufacturer' : `the ${dryRunResult.unaffectedManufacturers} manufacturers`} in this file {dryRunResult.unaffectedProducts === 1 ? 'is' : 'are'} not in it and would keep {dryRunResult.unaffectedProducts === 1 ? 'its' : 'their'} current pricing
                    {dryRunResult.unaffectedPrintedProducts > 0 ? ` (${dryRunResult.unaffectedPrintedProducts} of them printed)` : ''}.
                  </Typography>
                </Grid>
              )}
              {dryRunResult.removedPrices > 0 && (
                <Grid item xs={12}>
                  <Typography variant="caption" style={{color: '#f44336'}}>
                    {dryRunResult.removedPrices} existing price{dryRunResult.removedPrices === 1 ? '' : 's'} would be removed because the item/vendor appears in the file but that price line does not.
                  </Typography>
                </Grid>
              )}
              {dryRunResult.generatedSkuRows > 0 && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="textSecondary">
                    {dryRunResult.generatedSkuRows} row{dryRunResult.generatedSkuRows === 1 ? ' had' : 's had'} no SKU — one was filled in from the color or style so pricing can attach to a product option.
                  </Typography>
                </Grid>
              )}
              {dryRunResult.adoptedProducts > 0 && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="textSecondary">
                    {dryRunResult.adoptedProducts} product{dryRunResult.adoptedProducts === 1 ? '' : 's'} in this file matched your catalog almost exactly and will update the existing product{dryRunResult.adoptedProducts === 1 ? '' : 's'} — including price — instead of creating {dryRunResult.adoptedProducts === 1 ? 'a duplicate' : 'duplicates'}.
                  </Typography>
                  {(dryRunResult.adoptions || []).slice(0, 3).map((a, i) => (
                    <Typography key={i} variant="caption" style={{display: 'block', paddingLeft: 8}} color="textSecondary">
                      “{a.from.style_number || a.from.style_name}” ({a.from.manufacturer}) → existing “{a.to.style || a.to.trade_name}” ({a.to.manufacturer})
                    </Typography>
                  ))}
                  {(dryRunResult.adoptions || []).length > 3 && (
                    <Typography variant="caption" style={{display: 'block', paddingLeft: 8}} color="textSecondary">
                      …and {dryRunResult.adoptions.length - 3} more.
                    </Typography>
                  )}
                </Grid>
              )}
              {dryRunResult.duplicateWarnings?.warnings?.length > 0 && (
                <Grid item xs={12} style={{marginTop: 4}}>
                  <Typography variant="caption" style={{color: '#b26a00', fontWeight: 'bold', display: 'block'}}>
                    {dryRunResult.duplicateWarnings.probableDuplicates} “new” product{dryRunResult.duplicateWarnings.probableDuplicates === 1 ? ' looks' : 's look'} like {dryRunResult.duplicateWarnings.probableDuplicates === 1 ? 'a product' : 'products'} already in your catalog:
                  </Typography>
                  {dryRunResult.duplicateWarnings.warnings.slice(0, 5).map((w, i) => (
                    <Typography key={i} variant="caption" style={{display: 'block', paddingLeft: 8}}>
                      “{w.newProduct.style_number || w.newProduct.style_name}” ({w.newProduct.manufacturer}) ≈ existing “{w.existing.style || w.existing.trade_name}” ({w.existing.manufacturer}) — {Math.round(w.score * 100)}% match{w.existing.printed ? ', printed' : ''}
                    </Typography>
                  ))}
                  {dryRunResult.duplicateWarnings.warnings.length > 5 && (
                    <Typography variant="caption" style={{display: 'block', paddingLeft: 8}} color="textSecondary">
                      …and {dryRunResult.duplicateWarnings.warnings.length - 5} more.
                    </Typography>
                  )}
                  <Typography variant="caption" style={{color: '#b26a00', display: 'block', marginTop: 2}}>
                    Importing anyway creates them as separate products with their own QR codes. If they are the same product, merge them afterwards on the Duplicates page — or correct the vendor file and reprocess.
                  </Typography>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="contained" style={{margin: "10px"}} onClick={() => { setDryRunFile(null); setDryRunResult(null); }}>
            Close
          </Button>
          <Button
            variant="contained"
            disabled={dryRunLoading || !dryRunResult}
            style={{backgroundColor: "#142e3e", color: "#28c4fc", margin: "10px"}}
            onClick={() => { const f = dryRunFile; setImportDupWarnings(dryRunResult?.duplicateWarnings || null); setDryRunFile(null); setDryRunResult(null); setImportDialogFile(f); }}
          >
            Import
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={mappingBusy} fullWidth maxWidth="xs">
        <DialogContent>
          <div style={{display: 'flex', alignItems: 'center', gap: '14px', padding: '18px 6px'}}>
            <CircularProgress size={24} />
            <Typography variant="body2">Processing file with the vendor mapping…</Typography>
          </div>
        </DialogContent>
      </Dialog>
      <ResolveCodesDialog
        open={resolveOpen}
        issues={resolveIssues}
        onCancel={() => { setResolveOpen(false); setMappingBusy(false); }}
        onApply={onResolveApply}
        notify={notify}
      />
      <Dialog open={!!previewFile} onClose={closePreview} fullWidth maxWidth="lg">
        <DialogTitle>Preview — {previewFile?.filename}</DialogTitle>
        <DialogContent>
          {previewLoading || !previewData ? (
            <div style={{display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 0'}}>
              <CircularProgress size={20} />
              <Typography variant="body2">Loading data…</Typography>
            </div>
          ) : (
            <>
              <Typography variant="caption" color="textSecondary">
                {previewData.length - 1} row{previewData.length - 1 === 1 ? '' : 's'}
                {previewData.length - 1 > 100 ? ' (showing first 100)' : ''}
              </Typography>
              <TableContainer style={{maxHeight: isCorrectable(previewFile) ? '42vh' : '60vh', overflow: 'auto', marginTop: '8px'}}>
                <Table size="small" stickyHeader style={{width: 'auto', tableLayout: 'auto'}}>
                  <TableHead>
                    <TableRow>
                      {(previewData[0] ?? []).map((col, i) => (
                        <TableCell key={i} style={{fontWeight: 'bold', whiteSpace: 'nowrap', background: '#fafafa', padding: '6px 12px'}} title={col}>{col}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {previewData.slice(1, 101).map((row, r) => (
                      <TableRow key={r}>
                        {row.map((cell, c) => (
                          <TableCell key={c} style={{whiteSpace: 'nowrap', padding: '4px 12px'}}>{cell}</TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Correction panel — stays visible while the table above scrolls. */}
              {isCorrectable(previewFile) && (
                <div style={{marginTop: 12, paddingTop: 12, borderTop: '1px solid #eee'}}>
                  <Typography variant="subtitle2">See a problem? Describe it and we’ll fix it.</Typography>
                  <div style={{display: 'flex', gap: 8, alignItems: 'flex-start', marginTop: 8, flexWrap: 'wrap'}}>
                    <TextField
                      multiline
                      minRows={2}
                      variant="outlined"
                      placeholder="e.g. The cost column is actually sqft per carton, and the real cost went into retail price override."
                      value={feedbackText}
                      onChange={(e) => { setFeedbackText(e.target.value); setTriageResult(null); }}
                      style={{flex: '1 1 320px'}}
                    />
                    {!triageResult && (
                      <Button
                        variant="contained"
                        disabled={feedbackBusy || !feedbackText.trim()}
                        style={{whiteSpace: 'nowrap', backgroundColor: feedbackBusy || !feedbackText.trim() ? undefined : '#142e3e', color: '#28c4fc'}}
                        onClick={handleTriage}
                      >
                        {feedbackBusy ? <CircularProgress size={18} style={{color: '#28c4fc'}} /> : 'Analyze'}
                      </Button>
                    )}
                  </div>

                  {triageResult?.action === 'remap' && (
                    <div style={{marginTop: 12, padding: 12, background: '#e8f5e9', borderRadius: 4}}>
                      <Typography variant="body2" style={{fontWeight: 'bold', color: '#2e7d32'}}>
                        This can be fixed instantly — free (no reprocessing).
                      </Typography>
                      {triageResult.explanation && (
                        <Typography variant="caption" style={{display: 'block', marginTop: 4}}>{triageResult.explanation}</Typography>
                      )}
                      <Typography variant="caption" style={{display: 'block', marginTop: 8, color: 'rgba(0,0,0,0.6)'}}>
                        Columns changed: {(triageResult.columnsAffected || []).join(', ')}
                      </Typography>
                      {(triageResult.preview || []).length > 0 && (
                        <div style={{marginTop: 8}}>
                          {(triageResult.preview || []).map((p, i) => (
                            <div key={i} style={{marginTop: i ? 6 : 0}}>
                              {(triageResult.columnsAffected || []).map((col) => (
                                <Typography key={col} variant="caption" style={{display: 'block'}}>
                                  <span style={{color: 'rgba(0,0,0,0.5)'}}>{col}: </span>
                                  <span style={{textDecoration: 'line-through', color: '#c62828'}}>{String(p.before?.[col] ?? '') || '∅'}</span>
                                  {' → '}
                                  <span style={{color: '#2e7d32', fontWeight: 'bold'}}>{String(p.after?.[col] ?? '') || '∅'}</span>
                                </Typography>
                              ))}
                            </div>
                          ))}
                        </div>
                      )}
                      <div style={{marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap'}}>
                        <Button variant="contained" disabled={feedbackBusy} style={{backgroundColor: '#2e7d32', color: '#fff'}} onClick={handleApplyRemap}>
                          {feedbackBusy ? <CircularProgress size={18} style={{color: '#fff'}} /> : 'Apply correction (free)'}
                        </Button>
                        <Button variant="outlined" disabled={feedbackBusy} onClick={handleReprocess}>Reprocess instead</Button>
                        <Button variant="text" disabled={feedbackBusy} onClick={() => setTriageResult(null)}>Edit feedback</Button>
                      </div>
                    </div>
                  )}

                  {triageResult?.action === 'reprocess' && (
                    <div style={{marginTop: 12, padding: 12, background: '#fff3e0', borderRadius: 4}}>
                      <Typography variant="body2" style={{fontWeight: 'bold', color: '#b26a00'}}>
                        This needs a full reprocess — it re-runs the AI and uses credits.
                      </Typography>
                      {triageResult.reason && (
                        <Typography variant="caption" style={{display: 'block', marginTop: 4}}>{triageResult.reason}</Typography>
                      )}
                      <div style={{marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap'}}>
                        <Button variant="contained" disabled={feedbackBusy} style={{backgroundColor: '#b26a00', color: '#fff'}} onClick={handleReprocess}>
                          {feedbackBusy ? <CircularProgress size={18} style={{color: '#fff'}} /> : 'Reprocess (uses credits)'}
                        </Button>
                        <Button variant="text" disabled={feedbackBusy} onClick={() => setTriageResult(null)}>Edit feedback</Button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="contained" style={{margin: "10px"}} onClick={closePreview}>
            Close
          </Button>
          {/* Carry the flow forward from the preview instead of sending the user back to the row. */}
          {canImportFile(previewFile) && (
            <>
              <Button
                variant="contained"
                style={{margin: "10px", backgroundColor: '#fff'}}
                onClick={() => { const f = previewFile; closePreview(); handleDryRun(f); }}
              >
                Dry Run
              </Button>
              <Button
                variant="contained"
                style={{margin: "10px", backgroundColor: '#142e3e', color: '#28c4fc'}}
                onClick={() => { const f = previewFile; closePreview(); setImportDupWarnings(null); setImportDialogFile(f); }}
              >
                Import
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </div>
  )
}
